import differenceInDays from 'date-fns/fp/differenceInDays';
import format from 'date-fns/fp/format';
import getYear from 'date-fns/fp/getYear';
import isBefore from 'date-fns/fp/isBefore';
import isSameDay from 'date-fns/fp/isSameDay';
import isSameYear from 'date-fns/fp/isSameYear';
import millisecondsToMinutes from 'date-fns/fp/millisecondsToMinutes';
import moment from 'moment';
import { createContext, useCallback, useContext } from 'react';
import { ValueOf } from 'type-fest';

export const DATE_FORMATS = {
  MM_DD_YYYY: 'MM-DD-YYYY',
  DD_MM_YYYY: 'DD-MM-YYYY',
  YYYY_MM_DD: 'YYYY-MM-DD',
} as const;

export type DateFormat = ValueOf<typeof DATE_FORMATS>;

export const toMoment = (dateString: string) =>
  moment(dateString, moment.ISO_8601);

export type DateFormatValues = {
  dateFormat: string;
  dateTimeFormat: string;
  abbreviatedDateTimeFormat: string;
  timeFormat: string;
  dateAtTimeFormat: string;
  eventFormat: string;
  eventFormatNumerical: string;
  abbreviatedFormat: string;
  toDateString: (dateString: string) => string;
  toAbbreviatedDateString: (dateString: string) => string;
  toDateTimeString: (dateString: string) => string;
  toEventDateTimeString: (dateString: string) => string;
  toNumericalEventDateTimeString: (dateString: string) => string;
  unixToDateString: (unix: number) => string;
  generateRelativeDateTime: (
    from: number | string,
    isCompact?: boolean,
  ) => string;
};

const FORMATS = {
  [DATE_FORMATS.MM_DD_YYYY]: {
    dateFormat: 'MM-DD-YYYY',
    dateTimeFormat: 'MM-DD-YYYY hh:mmA',
    abbreviatedDateTimeFormat: 'M-D-YYYY h:mmA',
    timeFormat: 'h:mm A',
    dateAtTimeFormat: 'MM-DD-YYYY [at] hh:mm A',
    eventFormat: 'h:mm A, MMM D YYYY',
    eventFormatNumerical: 'h:mm A, MM-DD-YYYY',
    abbreviatedFormat: 'MMM D YYYY',
  },
  [DATE_FORMATS.DD_MM_YYYY]: {
    dateFormat: 'DD-MM-YYYY',
    dateTimeFormat: 'DD-MM-YYYY hh:mmA',
    abbreviatedDateTimeFormat: 'D-M-YYYY h:mmA',
    timeFormat: 'h:mm A',
    dateAtTimeFormat: 'DD-MM-YYYY [at] hh:mm A',
    eventFormat: 'h:mm A, D MMM YYYY',
    eventFormatNumerical: 'h:mm A, DD-MM-YYYY',
    abbreviatedFormat: 'D MMM YYYY',
  },
  [DATE_FORMATS.YYYY_MM_DD]: {
    dateFormat: 'YYYY-MM-DD',
    dateTimeFormat: 'YYYY-MM-DD hh:mmA',
    abbreviatedDateTimeFormat: 'YYYY-M-D h:mmA',
    timeFormat: 'h:mm A',
    dateAtTimeFormat: 'YYYY-MM-DD [at] hh:mm A',
    eventFormat: 'h:mm A, YYYY MMM D',
    eventFormatNumerical: 'h:mm A, YYYY-MM-DD',
    abbreviatedFormat: 'YYYY MMM D',
  },
} as const;

export const TemporalSettingsContext = createContext<{
  preferredDateFormat: DateFormat;
}>({
  preferredDateFormat: DATE_FORMATS.MM_DD_YYYY,
});

export const TemporalSettingsProvider = ({
  preferredDateFormat,
  children,
}: {
  preferredDateFormat: DateFormat;
  children: React.ReactNode;
}) => (
  <TemporalSettingsContext.Provider value={{ preferredDateFormat }}>
    {children}
  </TemporalSettingsContext.Provider>
);

export const useDateFormat = () => {
  const settings = useContext(TemporalSettingsContext);

  const formats =
    FORMATS[settings.preferredDateFormat ?? DATE_FORMATS.MM_DD_YYYY];

  const toDateString = useCallback(
    (dateString: string) => toMoment(dateString).format(formats.dateFormat),
    [formats],
  );

  const toAbbreviatedDateString = useCallback(
    (dateString: string) =>
      toMoment(dateString).format(formats.abbreviatedFormat),
    [formats],
  );

  const toDateTimeString = useCallback(
    (dateString: string) => toMoment(dateString).format(formats.dateTimeFormat),
    [formats],
  );

  const toEventDateTimeString = useCallback(
    (dateString: string) => toMoment(dateString).format(formats.eventFormat),
    [formats],
  );

  const toNumericalEventDateTimeString = useCallback(
    (dateString: string) =>
      toMoment(dateString).format(formats.eventFormatNumerical),
    [formats],
  );

  const unixToDateString = useCallback(
    (unix: number) => moment.unix(unix).format(formats.dateFormat),
    [formats],
  );

  const generateRelativeDateTime = useCallback(
    (fromEventDate: number | string, isCompact = false): string => {
      const oneMinute = 60_000;
      const oneHour = oneMinute * 60;
      const timezone = new Date()
        .toLocaleDateString('en-US', {
          day: '2-digit',
          timeZoneName: 'short',
        })
        .slice(4);
      const now = new Date().valueOf();
      let from;
      if (typeof fromEventDate === 'string') {
        from = new Date(fromEventDate).valueOf();
      } else {
        from = fromEventDate;
      }
      const diff = now - from;
      const m = millisecondsToMinutes(diff);
      const formatTime = format('h:mm a', new Date(from).valueOf());
      const compactDate = format(
        /**
         * @see https://github.com/date-fns/date-fns/blob/main/docs/unicodeTokens.md
         */
        formats.dateFormat.replaceAll('D', 'd').replaceAll('Y', 'y'),
        new Date(from).valueOf(),
      );
      const formatDate = format('MMMM d', new Date(from).valueOf());
      const diffInDays = differenceInDays(from, now);
      const sameYear = isSameYear(from, now);
      const fromYear = getYear(from);
      const isFromDayBeforeNow = isBefore(now, from);
      switch (true) {
        case diff < oneMinute:
          return 'just now';
        case diff >= oneMinute && diff < oneHour:
          return `${m} ${m === 1 ? 'minute' : 'minutes'} ago`;
        case isSameDay(from, now) && diff >= oneHour:
          return `today at ${formatTime} ${timezone}`;
        case isFromDayBeforeNow && (diffInDays === 0 || diffInDays === 1):
          // If its not a full 24 hours differenceInDays rounds down to 0
          return `yesterday at ${formatTime} ${timezone}`;
        case sameYear:
          return isCompact
            ? `${compactDate} ${formatTime} ${timezone}`
            : `${formatDate} at ${formatTime} ${timezone}`;
        case !sameYear:
          return isCompact
            ? `${compactDate} ${formatTime} ${timezone}`
            : `${formatDate}, ${fromYear} at ${formatTime} ${timezone}`;
        default:
          return `date for this event is unavailable`;
      }
    },
    [formats],
  );

  const result: DateFormatValues = {
    ...formats,
    toDateString,
    toAbbreviatedDateString,
    toDateTimeString,
    toEventDateTimeString,
    toNumericalEventDateTimeString,
    unixToDateString,
    generateRelativeDateTime,
  };

  return result;
};
